import React, { useEffect, useMemo, useState } from "react";
import {
  doc,
  onSnapshot,
  getDoc,
  setDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import emailjs from "@emailjs/browser";
import { db } from "../firebase";

export default function TicketSale() {
  const [authUid, setAuthUid] = useState("");
  const [allowedTours, setAllowedTours] = useState([]);
  const [selectedTour, setSelectedTour] = useState("");
  const [seatMap, setSeatMap] = useState({ seats: {} });
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [state, setState] = useState(0);
  const [confirmation, setConfirmation] = useState(false);
  const [error, setError] = useState("");
  const [loadingPurchase, setLoadingPurchase] = useState(false);
  const [lastPurchaseTotal, setLastPurchaseTotal] = useState(0);
  const [loadingTours, setLoadingTours] = useState(true);

  const toursMeta = [
    { id: "2026-tura-1", label: "Tura 1", hour: "13:00", number: 1 },
    { id: "2026-tura-2", label: "Tura 2", hour: "15:00", number: 2 },
    { id: "2026-tura-3", label: "Tura 3", hour: "17:00", number: 3 },
    { id: "2026-tura-4", label: "Tura 4", hour: "19:00", number: 4 },
  ];

  const unavalibleSeats = {
    r1: [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26],
    r2: [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27],
    r2a: [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28],
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (user) => {
      setAuthUid(user?.uid || "");
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    const loadTours = async () => {
      if (!authUid) {
        setAllowedTours([]);
        setSelectedTour("");
        setLoadingTours(false);
        return;
      }

      try {
        setLoadingTours(true);
        setError("");

        const userSnap = await getDoc(doc(db, "users2026", authUid));

        if (!userSnap.exists()) {
          setAllowedTours([]);
          setSelectedTour("");
          setError("Nie znaleziono konta użytkownika.");
          setLoadingTours(false);
          return;
        }

        const data = userSnap.data() || {};
        const subUsers = Object.values(data?.subUsers || {});
        const uniqueTours = new Set();

        subUsers.forEach((sub) => {
          const tours = Array.isArray(sub?.tours) ? sub.tours : [];
          tours.forEach((tourNumber) => {
            const parsed = Number(tourNumber);
            if (!Number.isNaN(parsed)) uniqueTours.add(parsed);
          });
        });

        const mappedTours = Array.from(uniqueTours)
          .sort((a, b) => a - b)
          .map((tourNumber) => toursMeta.find((t) => t.number === tourNumber))
          .filter(Boolean);

        setAllowedTours(mappedTours);

        if (mappedTours.length === 1) {
          setSelectedTour(mappedTours[0].id);
        } else if (mappedTours.length > 1) {
          setSelectedTour((prev) => {
            if (mappedTours.some((x) => x.id === prev)) return prev;
            return mappedTours[0].id;
          });
        } else {
          setSelectedTour("");
        }
      } catch (err) {
        console.error(err);
        setAllowedTours([]);
        setSelectedTour("");
        setError("Błąd przy wczytywaniu tur użytkownika.");
      } finally {
        setLoadingTours(false);
      }
    };

    loadTours();
  }, [authUid]);

  useEffect(() => {
    if (!selectedTour) {
      setSeatMap({ seats: {} });
      return;
    }

    const unsub = onSnapshot(doc(db, "gala", selectedTour), (snap) => {
      if (snap.exists()) {
        setSeatMap(snap.data() || { seats: {} });
      } else {
        setSeatMap({ seats: {} });
      }
    });

    return () => unsub();
  }, [selectedTour]);

  const selectedTourNumber = useMemo(() => {
    const match = String(selectedTour || "").match(/tura-(\d+)/);
    return match ? parseInt(match[1], 10) : 1;
  }, [selectedTour]);

  const currentTourData = useMemo(() => {
    return allowedTours.find((t) => t.id === selectedTour) || toursMeta.find((t) => t.id === selectedTour) || toursMeta[0];
  }, [allowedTours, selectedTour]);

  const hasManyTours = allowedTours.length > 1;

  const sortedRows = useMemo(() => {
    return Object.keys(seatMap?.seats || {}).sort((a, b) => {
      const [, nA = "", sA = ""] = String(a).match(/^r(\d+)([a-z]*)$/i) || [];
      const [, nB = "", sB = ""] = String(b).match(/^r(\d+)([a-z]*)$/i) || [];
      const diff = parseInt(nA, 10) - parseInt(nB, 10);
      return diff !== 0 ? diff : sA.localeCompare(sB);
    });
  }, [seatMap]);

  const getSortedSeatsForRow = (rowKey) => {
    return Object.values(seatMap?.seats?.[rowKey] || {}).sort((a, b) => {
      return Number(b?.seat || 0) - Number(a?.seat || 0);
    });
  };

  const isUnavailable = (seatData) => {
    if (!seatData) return true;
    if (seatData?.reserved === true) return true;
    if (seatData?.status !== "available") return true;
    return false;
  };

  const isSeatSelected = (row, seatNumber, docId) => {
    return selectedSeats.some(
      (x) =>
        x?.row === row &&
        Number(x?.num) === Number(seatNumber) &&
        String(x?.docId) === String(docId)
    );
  };

  const toggleSeat = (row, seatData) => {
    if (!seatData || isUnavailable(seatData) || !selectedTour) return;

    const seatId = `${selectedTour}_${row}_${seatData.seat}`;

    setSelectedSeats((prev) => {
      const exists = prev.find((x) => x.id === seatId);

      if (exists) {
        return prev.filter((x) => x.id !== seatId);
      }

      return [
        ...prev,
        {
          id: seatId,
          row,
          num: seatData.seat,
          price: seatData.price,
          isDiscount: !!seatData.reduced,
          tour: selectedTourNumber,
          docId: selectedTour,
        },
      ];
    });

    setError("");
  };

  const toggleDiscount = (idx) => {
    setSelectedSeats((prev) =>
      prev.map((s, i) =>
        i === idx ? { ...s, isDiscount: !s.isDiscount } : s
      )
    );
  };

  const groupedSeats = useMemo(() => {
    const grouped = {};

    selectedSeats.forEach((seat) => {
      if (!grouped[seat.tour]) grouped[seat.tour] = [];
      grouped[seat.tour].push(seat);
    });

    Object.keys(grouped).forEach((tour) => {
      grouped[tour] = grouped[tour].sort((a, b) => {
        if (a.row !== b.row) return String(a.row).localeCompare(String(b.row), "pl");
        return Number(a.num) - Number(b.num);
      });
    });

    return grouped;
  }, [selectedSeats]);

  const seatsGroupedForPayment = useMemo(() => {
    const grouped = {};

    selectedSeats.forEach((seat) => {
      const tourKey = Number(seat?.tour);
      if (!grouped[tourKey]) grouped[tourKey] = [];
      grouped[tourKey].push(seat);
    });

    const entries = Object.entries(grouped);

    if (entries.length <= 1) {
      return entries[0]?.[1] || [];
    }

    entries.sort((a, b) => {
      const seatsA = a[1] || [];
      const seatsB = b[1] || [];

      if (seatsB.length !== seatsA.length) {
        return seatsB.length - seatsA.length;
      }

      return Number(b[0]) - Number(a[0]);
    });

    return entries[0][1];
  }, [selectedSeats]);

  const grossPrice = useMemo(() => {
    return seatsGroupedForPayment.reduce(
      (sum, s) => sum + (s?.isDiscount ? 50 : s?.price || 0),
      0
    );
  }, [seatsGroupedForPayment]);

  const totalDiscount = useMemo(() => {
    if (seatsGroupedForPayment.length >= 6) return seatsGroupedForPayment.length * 10;
    if (seatsGroupedForPayment.length >= 4) return seatsGroupedForPayment.length * 5;
    return 0;
  }, [seatsGroupedForPayment]);

  const finalPrice = useMemo(() => {
    return grossPrice - totalDiscount;
  }, [grossPrice, totalDiscount]);

  const normalTicketsCount = useMemo(() => {
    return selectedSeats.filter((x) => x?.isDiscount === false).length;
  }, [selectedSeats]);

  const reducedTicketsCount = useMemo(() => {
    return selectedSeats.filter((x) => x?.isDiscount === true).length;
  }, [selectedSeats]);

  const formatSeatsForEmail = (seats = []) => {
    return seats
      .sort((a, b) => {
        if (a.tour !== b.tour) return Number(a.tour) - Number(b.tour);
        if (a.row !== b.row) return String(a.row).localeCompare(String(b.row), "pl");
        return Number(a.num) - Number(b.num);
      })
      .map((s) => {
        return `${s?.isDiscount ? "Bilet ulgowy" : "Bilet normalny"} - TURA ${s?.tour} - RZĄD ${String(s?.row || "").toUpperCase()} - MIEJSCE ${s?.num} - ${s?.finalPrice} zł`;
      })
      .join("\n");
  };

  const validateSeatsAvailability = async () => {
    if (!selectedSeats.length) {
      setError("Wybierz miejsca.");
      return { ok: false, validSeats: [] };
    }

    const docsToCheck = [...new Set(selectedSeats.map((s) => s.docId))];
    const docsData = {};

    for (const galaDocId of docsToCheck) {
      const snap = await getDoc(doc(db, "gala", galaDocId));
      docsData[galaDocId] = snap.exists() ? snap.data() : null;
    }

    const stillAvailable = [];
    const nowUnavailable = [];

    selectedSeats.forEach((seat) => {
      const galaData = docsData?.[seat.docId];
      const firebaseSeat = galaData?.seats?.[seat.row]?.[seat.num];

      if (
        firebaseSeat &&
        firebaseSeat?.reserved !== true &&
        firebaseSeat?.status === "available"
      ) {
        stillAvailable.push(seat);
      } else {
        nowUnavailable.push(seat);
      }
    });

    if (nowUnavailable.length > 0) {
      setSelectedSeats(stillAvailable);
      setError("Jedno lub więcej miejsc straciło dostępność. Wybierz miejsca jeszcze raz.");
      return { ok: false, validSeats: stillAvailable };
    }

    setError("");
    return { ok: true, validSeats: stillAvailable };
  };

  const handleGoToCart = async () => {
    const result = await validateSeatsAvailability();
    if (!result.ok) return;
    setState(1);
  };

  const handlePurchase = async () => {
    try {
      setLoadingPurchase(true);
      setError("");

      const auth = getAuth();
      const currentUser = auth?.currentUser;

      if (!currentUser) {
        setError("Musisz być zalogowany.");
        setLoadingPurchase(false);
        return;
      }

      const result = await validateSeatsAvailability();
      if (!result.ok) {
        setLoadingPurchase(false);
        return;
      }

      const validSeats = result.validSeats;

      const perSeatDiscount =
        totalDiscount > 0
          ? seatsGroupedForPayment.length >= 6
            ? 10
            : seatsGroupedForPayment.length >= 4
            ? 5
            : 0
          : 0;

      const withFinal = validSeats.map((s) => {
        const basePrice = s?.isDiscount ? 50 : (s?.price || 0);
        const finalSeatPrice = basePrice - perSeatDiscount;

        return {
          ...s,
          finalPrice: finalSeatPrice,
          seatKey: `${s?.docId}_${String(s?.row || "").toLowerCase()}_${Number(s?.num)}`,
        };
      });

      const purchaseRef = doc(collection(db, "gala-purchases2026"));

      await setDoc(
        purchaseRef,
        {
          createdAt: serverTimestamp(),
          customer: currentUser.uid,
          customerEmail: currentUser.email || "",
          seats: withFinal,
          seatKeys: withFinal.map((s) => s.seatKey),
          total: finalPrice,
          status: "pending",
        },
        { merge: true }
      );

      for (const seat of withFinal) {
        await setDoc(
          doc(db, "gala", seat.docId),
          {
            seats: {
              [seat.row]: {
                [seat.num]: {
                  seat: seat.num,
                  price: seat.price,
                  reduced: !!seat.isDiscount,
                  reserved: true,
                  status: "pending",
                  ownerUid: currentUser.uid,
                  purchaseId: purchaseRef.id,
                  finalPrice: seat.finalPrice,
                },
              },
            },
          },
          { merge: true }
        );
      }

      const seatsListForEmail = formatSeatsForEmail(withFinal);

      const emailParams = {
        to: currentUser.email || "",
        total: finalPrice,
        order: seatsListForEmail,
        topic: "Rezerwacja biletów na galę 2026"
      };

      try {
        await emailjs.send(
          "service_joaujb4",
          "template_y6lgu3l",
          emailParams,
          {
            publicKey: "_zhfTFF9xkoXaTdzT",
          }
        );
      } catch (emailError) {
        console.error("Błąd EmailJS:", emailError);
      }

      setLastPurchaseTotal(finalPrice);
      setSelectedSeats([]);
      setState(2);
    } catch (err) {
      console.error(err);
      setError("Błąd przy zamówieniu.");
    } finally {
      setLoadingPurchase(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        flexDirection: "column",
        backgroundColor: "rgb(240,240,240)",
        width: "100%",
        minHeight: "100vh",
        gap: "30px",
        position: "relative",
        overflow: "hidden",
        paddingBottom: "50px",
      }}
    >
      <h2 style={{ fontSize: "20px", fontWeight: "bold", marginTop: "50px", marginBottom: '20px' }}>Kup bilety na galę 2026</h2>

      {loadingTours && (
        <div
          style={{
            width: "90%",
            maxWidth: "800px",
            backgroundColor: "rgba(0,69,199,0.08)",
            border: "1px solid rgba(0,69,199,0.2)",
            color: "#0045C7",
            fontSize: "13px",
            fontWeight: "bold",
            borderRadius: "6px",
            padding: "10px 14px",
            marginTop: "10px",
          }}
        >
          Wczytywanie dostępnych tur...
        </div>
      )}

      {!loadingTours && !selectedTour && (
  <div
    style={{
      width: "92%",
      maxWidth: "720px",
      minHeight: "60vh",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      marginTop: "10px",
    }}
  >
    <div
      style={{
        width: "100%",
        backgroundColor: "rgb(255,255,255,0.88)",
        borderRadius: "12px",
        padding: "34px 26px",
        boxShadow:
          "rgba(0, 0, 0, 0.05) 0px 0px 0px 1.5px, rgb(209, 213, 219) 0px 0px 0px 1px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "16px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          width: "62px",
          height: "62px",
          borderRadius: "50%",
          backgroundColor: "rgba(0,69,199,0.08)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontSize: "28px",
          fontWeight: "bold",
          color: "#0045C7",
        }}
      >
        !
      </div>

      <h2
        style={{
          color: "rgb(30,30,30)",
          margin: 0,
          fontSize: "26px",
          fontWeight: "bold",
        }}
      >
        Brak dostępu do zakupu biletów
      </h2>

      <h3
        style={{
          color: "rgb(80,80,80)",
          fontSize: "15px",
          fontWeight: "normal",
          margin: 0,
          lineHeight: "1.6",
          maxWidth: "560px",
        }}
      >
        Na tym koncie nie znaleziono żadnej przypisanej tury gali.
        Skontaktuj się z biurem, jeśli uważasz, że to błąd.
      </h3>

      <button
        onClick={() => {
          window.location.href = "/";
        }}
        className="button-black"
        style={{
          width: "240px",
          height: "40px",
          fontSize: "13px",
          color: "white",
          borderRadius: "5px",
          marginTop: "8px",
        }}
      >
        Powrót na stronę główną
      </button>
    </div>
  </div>
)}

      {error && (
        <div
          style={{
            width: "90%",
            maxWidth: "800px",
            backgroundColor: "rgba(220,20,60,0.08)",
            border: "1px solid rgba(220,20,60,0.2)",
            color: "#DC143C",
            fontSize: "13px",
            fontWeight: "bold",
            borderRadius: "6px",
            padding: "10px 14px",
          }}
        >
          {error}
        </div>
      )}

      {!loadingTours && selectedTour && state === 0 && (
        <div
          className="gala-cont"
          style={{
            display: "flex",
            justifyContent: "space-around",
            alignItems: "flex-start",
            width: "100%",
            paddingBottom: "50px",
            gap: "25px",
            flexWrap: "wrap",
          }}
        >
<div
  style={{
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    marginBottom: "10px",
  }}
>
  {hasManyTours && (
    <div
      style={{
        width: "92%",
        maxWidth: "760px",
        backgroundColor: "rgba(0,69,199,0.08)",
        border: "1px solid rgba(0,69,199,0.16)",
        borderRadius: "8px",
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: "6px",
      }}
    >
      <h3
        style={{
          color: "#0045C7",
          fontSize: "13px",
          fontWeight: "bold",
          margin: 0,
          textAlign: "center",
        }}
      >
        Twoje dziecko/dzieci występują na więcej niż 1 turze
      </h3>

      <h3
        style={{
          color: "rgb(60,60,60)",
          fontSize: "12px",
          fontWeight: "normal",
          margin: 0,
          textAlign: "center",
          lineHeight: "1.55",
        }}
      >
        Wybierz bilety zarówno na jedną, jak i drugą turę, aby mieć pewne miejsce.
        W zamówieniu pojawi się suma za tylko 1 turę, druga jest dla Państwa darmowa.
      </h3>
    </div>
  )}

  <div
    style={{
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
      width: "100%",
    }}
  >
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        width: "260px",
        minHeight: "30px",
        maxHeight: "30px",
        padding: "20px",
        boxShadow: "rgba(0, 0, 0, 0.05) 0px 0px 0px 1.5px, rgb(209, 213, 219) 0px 0px 0px 1px",
        margin: "3px",
        borderRadius: "3px",
        backgroundColor: "rgb(255,255,255,0.75)",
      }}
    >
      <img src="event_time.png" style={{ width: "32.5px" }} alt="" />
      <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "flex-start", gap: "2px" }}>
        <h3 style={{ color: "rgb(35,35,35)", fontSize: "12px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
          2026
        </h3>
        <h3 style={{ color: "rgb(115,115,115)", fontSize: "11px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
          {currentTourData?.label}
        </h3>
      </div>
      <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "flex-start", gap: "2px" }}>
        <h3 style={{ color: "rgb(35,35,35)", fontSize: "11px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
          Godzina
        </h3>
        <h3 style={{ color: "rgb(115,115,115)", fontSize: "11px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
          {currentTourData?.hour}
        </h3>
      </div>
    </div>

    {hasManyTours && (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          gap: "8px",
          flexWrap: "wrap",
          width: "100%",
        }}
      >
            {allowedTours.map((tour) => (
              <button
                key={tour.id}
                onClick={() => {
                  setSelectedTour(tour.id);
                  setError("");
                }}
                style={{
                  minWidth: "100px",
                  height: "36px",
                  fontSize: "12px",
                  fontWeight: "bold",
                  color: selectedTour === tour.id ? "white" : "rgb(35,35,35)",
                  backgroundColor: selectedTour === tour.id ? "#0045C7" : "white",
                  borderRadius: "5px",
                  border:
                    selectedTour === tour.id
                      ? "2px solid #0045C7"
                      : "1px solid rgb(180,180,180)",
                  cursor: "pointer",
                  padding: "0 12px",
                  boxShadow:
                    selectedTour === tour.id
                      ? "none"
                      : "rgba(0, 0, 0, 0.03) 0px 0px 0px 1px",
                }}
              >
                {tour.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>

          <div
            style={{
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              overscrollBehaviorX: "contain",
              width: "100%",
              paddingBottom: "15px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "6px",
                minWidth: "700px",
                marginLeft: "70px",
                marginRight: "20px",
                paddingLeft: "20px",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  width: "75%",
                  height: "50px",
                  borderRadius: "5px",
                  border: "2px solid rgb(60,60,60)",
                  borderTop: "0px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <h3 style={{ color: "rgb(30,30,30)", fontSize: "22px", textAlign: "center", fontWeight: "bold", margin: 0 }}>
                  SCENA
                </h3>
              </div>

              <h3 style={{ color: "rgb(50,50,50)", fontSize: "13px", textAlign: "center", fontWeight: "bold", letterSpacing: "0.1px" }}>
                MIEJSCA WYŁĄCZONE Z SPRZEDAŻY
              </h3>

              {["r1", "r2", "r2a"].map((r) => (
                <div
                  key={r}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "4px",
                    marginBottom: r === "r2a" ? "20px" : "0",
                  }}
                >
                  <h3 style={{ color: "rgb(35,35,35)", fontSize: "12px", textAlign: "center", fontWeight: "bold", letterSpacing: "0.1px", padding: "0px 3px", minWidth: "25px" }}>
                    {r.toUpperCase()}
                  </h3>

                  {unavalibleSeats[r]?.sort((a, b) => b - a)?.map((s) => (
                    <div
                      key={s}
                      style={{
                        width: "21px",
                        height: "21px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        border: "1px solid #46464f",
                        backgroundColor: "#46464f",
                        cursor: "not-allowed",
                        borderRadius: "2px",
                      }}
                    >
                      <h3 style={{ color: "rgb(230,230,230)", textAlign: "center", fontWeight: "bold", fontSize: "10px", margin: 0 }}>
                        {s}
                      </h3>
                    </div>
                  ))}

                  <h3 style={{ color: "rgb(35,35,35)", fontSize: "12px", textAlign: "center", fontWeight: "bold", letterSpacing: "0.1px", padding: "0px 3px", minWidth: "25px" }}>
                    {r.toUpperCase()}
                  </h3>
                </div>
              ))}

              <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "6px" }}>
                {sortedRows.map((row) => (
                  <div
                    key={row}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <h3 style={{ color: "rgb(35,35,35)", fontSize: "12px", textAlign: "center", fontWeight: "bold", letterSpacing: "0.1px", padding: "0px 3px", minWidth: "25px" }}>
                      {row.toUpperCase()}
                    </h3>

                    {getSortedSeatsForRow(row).map((s) => {
                      const disabled = isUnavailable(s);
                      const selected = isSeatSelected(row, s?.seat, selectedTour);

                      return (
                        <div
                          key={`${row}_${s?.seat}`}
                          onClick={() => toggleSeat(row, s)}
                          style={{
                            width: "21px",
                            height: "21px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            border: selected
                              ? "1px solid #0045C7"
                              : disabled
                              ? "1px solid #46464f"
                              : "1px solid rgb(60,60,60)",
                            backgroundColor: selected
                              ? "#0045C7"
                              : disabled
                              ? "#46464f"
                              : "transparent",
                            borderRadius: "2px",
                            cursor: disabled ? "not-allowed" : "pointer",
                            transition: "0.2s",
                          }}
                        >
                          <h3
                            style={{
                              color: selected || disabled ? "rgb(230,230,230)" : "rgb(35,35,35)",
                              textAlign: "center",
                              fontWeight: "bold",
                              fontSize: "10px",
                              margin: 0,
                            }}
                          >
                            {s?.seat}
                          </h3>
                        </div>
                      );
                    })}

                    <h3 style={{ color: "rgb(35,35,35)", fontSize: "12px", textAlign: "center", fontWeight: "bold", letterSpacing: "0.1px", padding: "0px 3px", minWidth: "25px" }}>
                      {row.toUpperCase()}
                    </h3>
                  </div>
                ))}
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "20px",
                  marginTop: "15px",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      borderRadius: "2px",
                      border: "1px solid rgb(60,60,60)",
                    }}
                  />
                  <h3
                    style={{
                      color: "rgb(35,35,35)",
                      fontSize: "12px",
                      textAlign: "center",
                      fontWeight: "bold",
                      letterSpacing: "0.1px",
                      padding: "0px 3px",
                      marginTop: "1px",
                    }}
                  >
                    - Miejsce wolne
                  </h3>
                </div>

                <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      borderRadius: "2px",
                      border: "1px solid #46464f",
                      backgroundColor: "#46464f",
                    }}
                  />
                  <h3
                    style={{
                      color: "rgb(35,35,35)",
                      fontSize: "12px",
                      textAlign: "center",
                      fontWeight: "bold",
                      letterSpacing: "0.1px",
                      padding: "0px 3px",
                      marginTop: "1px",
                    }}
                  >
                    - Miejsce zajęte
                  </h3>
                </div>

                <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      borderRadius: "2px",
                      border: "1px solid #0045C7",
                      backgroundColor: "#0045C7",
                    }}
                  />
                  <h3
                    style={{
                      color: "rgb(35,35,35)",
                      fontSize: "12px",
                      textAlign: "center",
                      fontWeight: "bold",
                      letterSpacing: "0.1px",
                      padding: "0px 3px",
                      marginTop: "1px",
                    }}
                  >
                    - Miejsce wybrane
                  </h3>
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "6px", width: "100%", marginTop: "10px" }}>
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "center", gap: "6px", width: "300px" }}>
              <h3 style={{ color: "rgb(35,35,35)", fontSize: "16px", fontWeight: "bold", letterSpacing: "0.1px", marginBottom: "4px" }}>
                Wybrane miejsca:
              </h3>

              <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "center", width: "340px", maxHeight: "400px", overflowY: "scroll", overflowX: "hidden", marginTop: "5px" }}>
                {selectedSeats.length > 0 ? (
                  selectedSeats.map((s, i) => (
                    <div
                      key={s.id}
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "260px",
                        minHeight: "35px",
                        maxHeight: "35px",
                        padding: "20px",
                        boxShadow: "rgba(0, 0, 0, 0.05) 0px 0px 0px 1.5px, rgb(209, 213, 219) 0px 0px 0px 1px",
                        margin: "3px",
                        borderRadius: "3px",
                        backgroundColor: "rgb(255,255,255,0.75)",
                      }}
                    >
                      <img src="invit_blue.png" style={{ width: "26.5px" }} alt="" />
                      <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "flex-start", gap: "2px" }}>
                        <h3 style={{ color: "rgb(35,35,35)", fontSize: "11px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                          {s?.isDiscount ? "Bilet ulgowy" : "Bilet normalny"} - Tura {s?.tour}
                        </h3>
                        <h3 style={{ color: "rgb(115,115,115)", fontSize: "10px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                          Koszt: {s.isDiscount ? 50 : s.price} PLN
                        </h3>
                        <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", justifyContent: "center" }}>
                          <h3 style={{ color: "rgb(115,115,115)", fontSize: "10px", fontWeight: "bold", letterSpacing: "0.1px", marginTop: "5px", marginBottom: 0 }}>
                            Bilet ulgowy do 16 r.ż.
                          </h3>
                          <input
                            type="checkbox"
                            style={{ marginTop: "3px" }}
                            checked={s.isDiscount}
                            onChange={() => setConfirmation({ active: true, s: s, i: i })}
                          />
                        </label>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "flex-start", gap: "2px" }}>
                        <h3 style={{ color: "rgb(35,35,35)", fontSize: "10px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                          Rząd: {s?.row?.toUpperCase()}
                        </h3>
                        <h3 style={{ color: "rgb(115,115,115)", fontSize: "10px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                          Miejsce: {s?.num}
                        </h3>
                      </div>
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "220px",
                      minHeight: "35px",
                      maxHeight: "35px",
                      padding: "20px",
                      boxShadow: "rgba(0, 0, 0, 0.05) 0px 0px 0px 1.5px, rgb(209, 213, 219) 0px 0px 0px 1px",
                      margin: "3px",
                      borderRadius: "3px",
                      backgroundColor: "rgb(255,255,255,0.75)",
                    }}
                  >
                    <h3 style={{ color: "rgb(35,35,35)", fontSize: "12px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                      Brak wybranych miejsc
                    </h3>
                  </div>
                )}
              </div>

              <div style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", gap: "6px", marginTop: "10px" }}>
                <button
                  style={{
                    width: "125px",
                    height: "41px",
                    fontSize: "13px",
                    color: "white",
                    borderRadius: "5px",
                    cursor: selectedSeats.length === 0 ? "not-allowed" : "pointer",
                  }}
                  onClick={handleGoToCart}
                  className="button-black"
                  disabled={selectedSeats.length === 0}
                >
                  Przejdź dalej
                </button>

                <div
                  style={{
                    boxShadow: "rgba(0, 0, 0, 0.05) 0px 0px 0px 1.5px, rgb(209, 213, 219) 0px 0px 0px 1px",
                    border: "2px solid rgba(0,0,0,0)",
                    width: "35px",
                    height: "35px",
                    borderRadius: "5px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                    backgroundImage: "url(cart.png)",
                    backgroundSize: "80% 80%",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "50% 50%",
                    backgroundColor: "white",
                  }}
                >
                  <h3 style={{ fontSize: "12px", color: "#0045C7", fontWeight: "bold", marginTop: "-8px", marginLeft: "-2px", marginBottom: 0 }}>
                    {selectedSeats.length}
                  </h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedTour && state === 1 && (
        <div
          className="cart_panel"
          style={{
            display: "flex",
            width: "90%",
            gap: "25px",
            justifyContent: "center",
            alignItems: "flex-start",
            minHeight: "100vh",
            marginBottom: "50px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "center", gap: "6px" }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "center",
                marginTop: "5px",
                backgroundColor: "rgb(255,255,255,0.75)",
                padding: "30px",
                boxShadow: "rgba(0, 0, 0, 0.05) 0px 0px 0px 1.5px, rgb(209, 213, 219) 0px 0px 0px 1px",
                backgroundImage: "url(dancer_shadow3.png)",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "50% 50%",
              }}
            >
              <h3 style={{ color: "rgb(95,95,95)", fontSize: "14px", fontWeight: "bold", letterSpacing: "0.1px", marginBottom: "27.5px", width: "100%", textAlign: "left" }}>
                Twoje bilety
              </h3>

              <div style={{ height: "1px", backgroundColor: "rgb(225,225,225)", width: "100%", marginBottom: "10px" }} />

              {selectedSeats.map((s, i) => (
                <div
                  key={s.id}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "90%",
                    maxWidth: "430px",
                    minHeight: "40px",
                    maxHeight: "40px",
                    padding: "30px",
                    boxShadow: "rgba(0, 0, 0, 0.05) 0px 0px 0px 1.5px, rgb(209, 213, 219) 0px 0px 0px 1px",
                    margin: "3px",
                    borderRadius: "4px",
                    backgroundColor: "rgb(255,255,255,0.75)",
                    position: "relative",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", gap: "25px" }}>
                    <img className="cart_icon" src="invit_blue.png" alt="" />
                    <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "flex-start", gap: "2px" }}>
                      <h3 className="cart_title" style={{ color: "rgb(35,35,35)", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                        {s?.isDiscount ? "Bilet ulgowy" : "Bilet normalny"} - Tura {s?.tour}
                      </h3>
                      <h3 style={{ color: "rgb(115,115,115)", fontSize: "12px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                        Koszt: {s?.isDiscount ? "50" : s?.price} PLN
                      </h3>
                      <div style={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", gap: "5px", marginTop: "10px" }}>
                        <input
                          type="checkbox"
                          style={{ width: "17px", height: "17px", margin: "0" }}
                          onClick={() => setConfirmation({ active: true, s: s, i: i })}
                          checked={s?.isDiscount}
                          readOnly
                        />
                        <h3 className="cart_redu" style={{ color: "rgb(35,35,35)", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                          Bilet ulgowy (do 16 r.ż.)
                        </h3>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "flex-start", gap: "2px" }}>
                    <h3 className="cart_ticket" style={{ color: "rgb(35,35,35)", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                      Rząd: {s?.row?.toUpperCase()}
                    </h3>
                    <h3 className="cart_ticket" style={{ color: "rgb(115,115,115)", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                      Miejsce: {s?.num}
                    </h3>
                  </div>
                </div>
              ))}

              <button
                style={{
                  width: "100%",
                  height: "35px",
                  fontSize: "13px",
                  color: "white",
                  borderRadius: "5px",
                  cursor: "pointer",
                  marginTop: "20px",
                }}
                className="button-black"
                onClick={() => setState(0)}
              >
                Wróć do wyboru miejsc
              </button>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              alignItems: "flex-start",
              width: "90%",
              maxWidth: "300px",
              minHeight: "400px",
              padding: "25px",
              boxShadow: "rgba(0, 0, 0, 0.05) 0px 0px 0px 1.5px, rgb(209, 213, 219) 0px 0px 0px 1px",
              margin: "3px",
              borderRadius: "3px",
              backgroundColor: "rgb(255,255,255,0.75)",
              marginTop: "4px",
              backgroundImage: "url(dancer_shadow2.png)",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "50% 50%",
            }}
          >
            <h3 style={{ color: "rgb(95,95,95)", fontSize: "14px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
              Podsumowanie
            </h3>

            <div style={{ height: "1px", backgroundColor: "rgb(225,225,225)", width: "100%" }} />

            <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "flex-start", gap: "15px", width: "100%" }}>
              <h3 style={{ color: "rgb(95,95,95)", fontSize: "15px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                Bilety:
              </h3>

              <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", width: "100%" }}>
                <h3 style={{ color: "rgb(115,115,115)", fontSize: "12px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                  Bilety normalne
                </h3>
                <h3 style={{ color: "rgb(95,95,95)", fontSize: "13px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                  {normalTicketsCount}
                </h3>
              </div>

              <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", width: "100%" }}>
                <h3 style={{ color: "rgb(115,115,115)", fontSize: "12px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                  Bilety ulgowe
                </h3>
                <h3 style={{ color: "rgb(95,95,95)", fontSize: "13px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                  {reducedTicketsCount}
                </h3>
              </div>
            </div>

            <div style={{ height: "1px", backgroundColor: "rgb(225,225,225)", width: "100%" }} />

            <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "flex-start", gap: "15px", width: "100%" }}>
              <h3 style={{ color: "rgb(95,95,95)", fontSize: "15px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                Razem:
              </h3>

              <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", width: "100%" }}>
                <h3 style={{ color: "rgb(115,115,115)", fontSize: "12px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                  Koszt biletów
                </h3>
                <h3 style={{ color: "rgb(95,95,95)", fontSize: "13px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                  {grossPrice} ZŁ
                </h3>
              </div>

              <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", width: "100%" }}>
                <h3 style={{ color: "rgb(115,115,115)", fontSize: "12px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                  Zniżka
                </h3>
                <h3 style={{ color: "rgb(95,95,95)", fontSize: "13px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                  {totalDiscount} ZŁ
                </h3>
              </div>
            </div>

            <div style={{ height: "1px", backgroundColor: "rgb(225,225,225)", width: "100%" }} />

            <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", width: "100%" }}>
              <h3 style={{ color: "rgb(95,95,95)", fontSize: "15px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                Końcowo:
              </h3>
              <h3 style={{ color: "rgb(95,95,95)", fontSize: "15px", fontWeight: "bold", letterSpacing: "0.1px", margin: 0 }}>
                {finalPrice} ZŁ
              </h3>
            </div>

            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "2px", width: "100%", marginTop: "10px" }}>
              <button
                style={{
                  width: "80%",
                  height: "35px",
                  fontSize: "13px",
                  color: "white",
                  borderRadius: "5px",
                  cursor: loadingPurchase ? "default" : "pointer",
                  opacity: loadingPurchase ? "0.7" : "1",
                }}
                className="button-black"
                onClick={handlePurchase}
                disabled={loadingPurchase}
              >
                {loadingPurchase ? "Rezerwowanie..." : "Zamów bilety"}
              </button>
            </div>
          </div>
        </div>
      )}

      {state === 2 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: "18px",
            width: "100%",
            minHeight: "70vh",
            padding: "20px",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              width: "92%",
              maxWidth: "720px",
              backgroundColor: "rgb(255,255,255,0.85)",
              borderRadius: "10px",
              padding: "30px 25px",
              boxShadow: "rgba(0, 0, 0, 0.05) 0px 0px 0px 1.5px, rgb(209, 213, 219) 0px 0px 0px 1px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "16px",
              textAlign: "center",
            }}
          >
            <h2
              style={{
                color: "#0045C7",
                margin: 0,
                fontSize: "28px",
                fontWeight: "bold",
              }}
            >
              Rezerwacja przyjęta
            </h2>

            <h3
              style={{
                color: "rgb(30,30,30)",
                fontSize: "15px",
                fontWeight: "normal",
                margin: 0,
                lineHeight: "1.6",
                maxWidth: "620px",
              }}
            >
              Twoje miejsca zostały wstępnie zarezerwowane i oznaczone jako oczekujące.
            </h3>

            <div
              style={{
                width: "100%",
                maxWidth: "560px",
                backgroundColor: "rgba(0,69,199,0.06)",
                border: "1px solid rgba(0,69,199,0.12)",
                borderRadius: "8px",
                padding: "18px 16px",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              <h3
                style={{
                  color: "#0045C7",
                  fontSize: "16px",
                  fontWeight: "bold",
                  margin: 0,
                }}
              >
                Informacje do płatności
              </h3>

              <h3
                style={{
                  color: "rgb(45,45,45)",
                  fontSize: "14px",
                  fontWeight: "normal",
                  margin: 0,
                  lineHeight: "1.55",
                }}
              >
                Aby potwierdzić rezerwację, opłać zamówienie przelewem.
              </h3>

              <h3
                style={{
                  color: "rgb(45,45,45)",
                  fontSize: "14px",
                  fontWeight: "normal",
                  margin: 0,
                  lineHeight: "1.55",
                }}
              >
                Rezerwacja nieopłacona w ciągu <b>72 godzin</b> może zostać anulowana.
              </h3>

              <div
                style={{
                  width: "100%",
                  height: "1px",
                  backgroundColor: "rgba(0,0,0,0.08)",
                  margin: "4px 0",
                }}
              />

              <h3
                style={{
                  color: "rgb(30,30,30)",
                  fontSize: "14px",
                  fontWeight: "bold",
                  margin: 0,
                }}
              >
                Kwota do zapłaty: {lastPurchaseTotal} zł
              </h3>
            </div>

            <button
              onClick={() => window.location.href = "/rezerwacje"}
              className="button-black"
              style={{
                width: "240px",
                height: "40px",
                fontSize: "13px",
                color: "white",
                borderRadius: "5px",
                marginTop: "8px",
              }}
            >
              Przejdź do swoich biletów
            </button>
          </div>
        </div>
      )}

      {confirmation?.active === true && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "100%",
            backgroundColor: "rgb(0,0,0,0.5)",
            position: "fixed",
            top: "0",
            left: "0",
            zIndex: 30,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              width: "350px",
              height: "150px",
              backgroundColor: "rgb(240,240,240)",
              boxShadow: "rgba(0, 0, 0, 0.05) 0px 0px 0px 1.5px, rgb(209, 213, 219) 0px 0px 0px 1px",
              borderRadius: "5px",
              gap: "8px",
            }}
          >
            <h3 style={{ color: "#0045C7", fontSize: "14px", fontWeight: "bold", letterSpacing: "0.1px", textAlign: "center", margin: 0 }}>
              {confirmation?.s?.isDiscount ? "Anulowanie ulgi" : "Ulga przysługuje osobom tylko do 16 r.ż."}
            </h3>

            <h3 style={{ color: "rgb(35,35,35)", fontSize: "11px", fontWeight: "bold", letterSpacing: "0.1px", textAlign: "center", margin: 0 }}>
              {confirmation?.s?.isDiscount
                ? "Chcę anulować ulgę z tego biletu."
                : "Jestem świadomy, że ulga przysługuje wyłącznie osobom do 16 r.ż. i akceptuję to."}
            </h3>

            <div style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", gap: "10px", width: "100%", marginTop: "15px" }}>
              <button
                style={{
                  width: "100px",
                  height: "30px",
                  fontSize: "13px",
                  color: "white",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
                className="button-black"
                onClick={() => {
                  toggleDiscount(confirmation?.i);
                  setConfirmation(false);
                }}
              >
                Potwierdź
              </button>

              <button
                style={{
                  width: "100px",
                  height: "30px",
                  fontSize: "13px",
                  color: "rgb(35,35,35)",
                  borderRadius: "5px",
                  cursor: "pointer",
                  border: "0px solid rgb(0,0,0,0)",
                  boxShadow: "rgba(1, 1, 1, 0.05) 0px 0px 0px 1.5px, rgb(209, 213, 219) 0px 0px 0px 1px",
                  backgroundColor: "rgb(240,240,240)",
                }}
                onClick={() => setConfirmation(false)}
              >
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}